import { Vue, Prop, Watch } from 'vue-property-decorator';
import Component from 'vue-class-component';
import { Scenario } from '@/models/scenario';
// @ts-ignore pas de d.ts pour la build dist
import Plyr from 'plyr/dist/plyr.js';
import { Storage } from 'aws-amplify';
import { Video } from '@/models/video';
import { Logger } from 'aws-amplify';
const logger = new Logger('ScenarioPlayer');

@Component({ components: {} })
export default class ScenarioPlayer extends Vue {
  @Prop({ type: Object }) private readonly scenario?: Scenario;
  @Prop({ type: Object }) private readonly video?: Video;

  private videoUrl: string | null = null;
  private player: any = null;
  private isDownloading = false;

  private videoReady = false;
  private isMuted = false;
  private rates: number[] = [0.5, 0.75, 1, 1.25, 1.5, 2];

  public get playbackRateLabel(): string {
    const rate = this.player && 'speed' in this.player ? this.player.speed : 1;
    return `${rate}x`;
  }

  get plyrOptions(): any {
    const $t = this.$t.bind(this);

    return {
      autoplay: false,
      clickToPlay: true,
      hideControls: true,
      seekTime: 10,
      resetOnEnd: true,
      disableContextMenu: false,
      loadSprite: true,
      iconPrefix: 'plyr',
      iconUrl: 'https://cdn.plyr.io/3.8.3/plyr.svg',
      blankVideo: 'https://cdn.plyr.io/static/blank.mp4',
      controls: [
        'play-large',
        'restart',
        'rewind',
        'play',
        'fast-forward',
        'progress',
        'current-time',
        'mute',
        'volume',
        'captions',
        'settings',
        'pip',
        'airplay',
        //'download',
        'fullscreen',
      ],
      tooltips: {
        controls: true,
        seek: true,
      },
      i18n: {
        restart: $t('video.restart').toString(),
        rewind: $t('video.rewind').toString(),
        play: $t('video.play').toString(),
        pause: $t('video.pause').toString(),
        fastForward: $t('video.fastForward').toString(),
        seek: $t('video.seek').toString(),
        seekLabel: $t('video.seekLabel').toString(),
        played: $t('video.played').toString(),
        buffered: $t('video.buffered').toString(),
        currentTime: $t('video.currentTime').toString(),
        duration: $t('video.duration').toString(),
        volume: $t('video.volume').toString(),
        mute: $t('video.mute').toString(),
        unmute: $t('video.unmute').toString(),
        enableCaptions: $t('video.enableCaptions').toString(),
        disableCaptions: $t('video.disableCaptions').toString(),
        settings: $t('video.settings').toString(),
        speed: $t('video.speed').toString(),
        normal: $t('video.normal').toString(),
        quality: $t('video.quality').toString(),
        loop: $t('video.loop').toString(),
        enterFullscreen: $t('video.enterFullscreen').toString(),
        exitFullscreen: $t('video.exitFullscreen').toString(),
        download: $t('video.download').toString(),
        reset: $t('common.reset').toString(),
        disabled: $t('global.need_video').toString(),
        enabled: $t('global.activate').toString(),
        all: $t('global.timelapse').toString(),
        start: $t('global.start').toString(),
        end: $t('global.end').toString(),
      },
    };
  }

  public get shareUrl(): string {
    return this.buildShareLink(this.scenario || this.video);
  }

  public get videoPlayerRef(): string {
    if (this.scenario) return `videoPlayer-${this.scenario.id}`;
    if (this.video) return `videoPlayer-${this.video.projectId}-${this.video.cameraId}`;
    return '';
  }

  public get scenarioPlayerRef(): string {
    if (this.scenario) return `scenario-player-${this.scenario.id}`;
    if (this.video) return `scenario-player-${this.video.projectId}-${this.video.cameraId}`;
    return '';
  }

  public mounted(): void {
    this.initPlayer();
  }

  public beforeDestroy(): void {
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
  }

  public show(): void {
    const ref = this.$refs[this.scenarioPlayerRef] as any;
    if (ref?.show) ref.show();
  }

  private async initPlayer() {
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }

    let promiseUrl: Promise<string> | undefined;

    if (this.video?.key) {
      promiseUrl = Storage.get(this.video.key) as Promise<string>;
    } else if (this.scenario?.video.key) {
      promiseUrl = Storage.get(this.scenario.video.key) as Promise<string>;
    }

    if (!promiseUrl) return;

    this.videoReady = false;

    try {
      const url = await promiseUrl;
      this.videoUrl = url as string;

      await this.$nextTick();

      const ref = this.$refs[this.videoPlayerRef] as HTMLVideoElement | HTMLVideoElement[] | undefined;
      const el = Array.isArray(ref) ? ref[0] : ref;

      if (!el) return;

      this.player = new Plyr(el, this.plyrOptions);

      this.player.source = {
        type: 'video',
        sources: [
          {
            src: this.videoUrl,
            type: 'video/mp4',
          },
        ],
      };

      this.player.on('ready', () => {
        this.videoReady = true;
      });
      this.player.on('loadeddata', () => {
        this.videoReady = true;
      });
      this.player.on('error', (e: any) => logger.error('video error', e));

      // Auto-open si le scénario est dans la query
      if (this.scenario && this.$route.query.scenario === this.scenario.id) {
        const scenarioPlayer = this.$refs[this.scenarioPlayerRef] as any;
        scenarioPlayer?.show?.();
      }
    } catch (e) {
      logger.error('Failed to init scenario player', e);
      this.videoReady = true; // éviter skeleton infini
    }
  }

  // NEW: outils overlay
  private toggleMute(): void {
    if (!this.player) return;
    this.isMuted = !this.isMuted;
    this.player.muted = this.isMuted;
  }

  private setRate(rate: number): void {
    if (!this.player) return;
    if ('speed' in this.player) {
      this.player.speed = rate;
    }
    this.$forceUpdate();
  }

  private async requestPiP(): Promise<void> {
    try {
      const el = this.$refs[this.videoPlayerRef] as any as HTMLVideoElement;
      if (!el) return;
      if ((document as any).pictureInPictureElement) {
        (document as any).exitPictureInPicture?.();
      } else {
        await (el as any).requestPictureInPicture?.();
      }
    } catch (e) {
      logger.warn('PiP not available', e);
    }
  }

  // Copie de lien
  public async copyShareLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.shareUrl);
      this.$bvToast.toast((this.$t('global.copied') as string) || 'Lien copié', {
        title: 'Timelapse',
        variant: 'info',
        autoHideDelay: 2000,
      });
    } catch {
      window.prompt('Copiez ce lien :', this.shareUrl);
    }
  }

  // Téléchargement (garde ton implémentation si tu préfères)
  public async onVideoDownload(evt?: MouseEvent): Promise<void> {
    evt?.preventDefault();
    this.isDownloading = true;
    try {
      if (this.video?.key) {
        const response: any = await Storage.get(this.video.key, { download: true });
        // @ts-ignore
        saveAs(new Blob([response.Body], { type: 'video/mp4' }), 'video.mp4');
      } else if (this.scenario?.video.key) {
        const response: any = await Storage.get(this.scenario.video.key, { download: true });
        // @ts-ignore
        saveAs(new Blob([response.Body], { type: 'video/mp4' }), 'video.mp4');
      }
    } catch (error) {
      console.error('Error downloading video:', error);
    } finally {
      this.isDownloading = false;
    }
  }

  public buildShareLink(object: Scenario | Video | undefined): string {
    if (!object) return '';
    if ('video' in object) {
      return `${process.env.VUE_APP_API_ENDPOINT}/scenario/${object.id}/viewer`;
    } else {
      return `${process.env.VUE_APP_API_ENDPOINT}/timelapse/${object.projectId}/${object.cameraId}/viewer`;
    }
  }

  private hideModal(): void {
    const ref = this.$refs[this.scenarioPlayerRef] as any;
    if (ref && typeof ref.hide === 'function') {
      ref.hide();
    }
  }

  public shareFacebook(): void {
    window.open(
      'https://www.facebook.com/sharer/sharer.php?u=' + this.shareUrl,
      '',
      'menubar=no,toolbar=no,resizable=yes,scrollbars=yes,height=600,width=600'
    );
  }
  public shareTwitter(): void {
    window.open(
      'https://twitter.com/intent/tweet?text=' + this.shareUrl,
      '',
      'menubar=no,toolbar=no,resizable=yes,scrollbars=yes,height=600,width=600'
    );
  }
  public shareLinkedin(): void {
    window.open(
      'https://www.linkedin.com/sharing/share-offsite/?url=' + this.shareUrl,
      '',
      'menubar=no,toolbar=no,resizable=yes,scrollbars=yes,height=600,width=600'
    );
  }
  public shareEmail(): void {
    window.open('mailto:someone@example.com?subject=Photo%20du%20chantier&body=' + encodeURIComponent(this.shareUrl));
  }

  @Watch('video')
  @Watch('scenario')
  onSourceChange() {
    this.initPlayer();
  }

  @Watch('$i18n.locale')
  onLocaleChanged() {
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
    this.initPlayer();
  }
}
